import { lambdaEndpoints } from '../aws-config';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface QueryHistoryItem {
  user_id: string;
  timestamp: number;
  query_id: string;
  query_type: 'user' | 'ai_generated';
  query_text: string;
  prompt?: string;
  file_name: string;
  model_info?: string;
  execution_time: number;
  success: boolean;
  result_count: number;
  error_message?: string;
}

export interface QueryResultsInfo {
  metadata: any;
  results: Array<{
    row_group_index: number;
    file_name: string;
    key: string;
    size: number;
    last_modified: string;
  }>;
  errors: Array<{
    row_group_index: number;
    file_name: string;
    key: string;
    last_modified: string;
  }>;
  total_files: number;
  completed_row_groups: number;
  failed_row_groups: number;
}

export interface QueryResultsResponse {
  success: boolean;
  query_folder?: string;
  query_info?: QueryResultsInfo;
  aggregated_results?: any;
  presigned_urls?: Array<{
    row_group_index: number;
    presigned_url: string;
    size: number;
    last_modified: string;
  }>;
  total_urls?: number;
  total_rows?: number;
  expires_in_seconds?: number;
  aggregated_rows?: number;
  results_count?: number;
  errors_count?: number;
  status?: string;
  metadata?: any;
  completed_row_groups?: number;
  failed_row_groups?: number;
}

export interface QueryHistoryResponse {
  success: boolean;
  queries?: QueryHistoryItem[];
  count?: number;
  last_key?: string;
  error?: string;
}

export interface FileInfo {
  fileName: string;
  fileSize: number;
  uploadDate: string;
  lastModified?: string;
  contentType?: string;
  originalExtension?: string;  // For faded display in UI
  currentExtension?: string;
  originalFilename?: string;
  s3Key?: string;  // Actual S3 key for analysis
  isInFolder?: boolean;  // Whether file is in a folder
  folderPath?: string;  // Folder path if in a folder
  isEnhanced?: boolean;  // Whether file uses enhanced processing (large files)
  sizeMB?: number;  // File size in MB
  isMultipart?: boolean;  // Whether this is a multipart dataset (100 worker files)
  fileCount?: number;  // Number of worker files in multipart dataset
  // SQL Import folder fields
  type?: string;  // 'sql-folder' for SQL import folders
  isSqlImportFolder?: boolean;  // Whether this is a SQL import folder
  isSqlImport?: boolean;  // Whether this is a SQL import file
  jobName?: string;  // SQL job name
  jobId?: string;  // SQL job ID
  partCount?: number;  // Number of part files in SQL folder
  totalRows?: number;  // Total rows across all parts
  fileType?: 'data' | 'audio' | 'pdf' | 'video' | 'image';  // Source file type
}

export interface AnalysisResult {
  query: string;
  result: any[];
  executionTime: number;
  rowCount: number;
  totalRowsScanned?: number; // Total rows scanned (for search operations)
  totalRowsAvailable?: number; // Total rows available before limiting to 1000
  resultTruncated?: boolean; // Whether results were truncated due to limit
  isCached?: boolean;
  s3Only?: boolean; // If true, results are only in S3 (>1000 rows) - frontend must fetch from s3Results.display_url
  s3Results?: {
    display_url: string; // Presigned URL for fetching full results
    download_url: string; // Presigned URL for downloading as file
    s3_key: string;
    compressed_size_mb: number;
    full_row_count: number;
  };
  chunkInfo?: {
    totalChunks: number;
    successfulChunks: number;
    failedChunks: number;
    totalRows: number;
    totalColumns: number;
    folderPath: string;
    processingType?: 'chunk_folder' | 'row_group_parallel';
    metadata?: any; // Parquet metadata for row group processing
  };
}

export interface DetailedExplanation {
  purpose?: string;
  data_processing?: string;
  column_mapping?: string;
  expected_output?: string;
}

export interface QueryGenerationResult {
  success: boolean;
  query?: string;
  explanation?: string;
  detailed_explanation?: DetailedExplanation;
  error?: string;
  debug?: {
    raw_response?: string;
    token_usage?: {
      total_tokens?: number;
      prompt_tokens?: number;
      completion_tokens?: number;
      completion_tokens_details?: {
        reasoning_tokens?: number;
      };
    };
    finish_reason?: string;
    model?: string;
    validation_passed?: boolean;
    error_type?: string;
    timestamp?: number;
  };
}


export interface UploadResponse {
  success: boolean;
  fileName?: string;
  uploadUrl?: string;
  message?: string;
  error?: string;
  log_key?: string;
  warnings?: string[];
  errors?: string[];
  rows_processed?: number;
  rows_skipped?: number;
  // Async upload pattern fields
  s3Key?: string;
  uploadDuration?: number;
  async?: boolean;
}

export interface FileListResponse {
  success: boolean;
  files: FileInfo[];
  error?: string;
}

export interface ProcessingFileInfo {
  filename: string;
  original_filename: string;
  upload_date: string;
  file_size: number;
  status: 'processing' | 'failed';
  chunk_info?: {
    current_chunks: number;
    expected_chunks?: number;
    total_rows?: number;
    progress_percent?: number;
    dataset_folder: string;
    chunks: Array<{
      filename: string;
      size: number;
      last_modified: string;
    }>;
  };
  s3_key: string;
  error_message?: string;
}

export interface ProcessingStatusResponse {
  success: boolean;
  processing_files: ProcessingFileInfo[];
  total: number;
  timestamp: string;
  error?: string;
}



class LambdaService {
  private cachedFiles: FileInfo[] | null = null;

  // Get authentication headers with debugging
  async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      // console.log('🔐 Getting auth session...');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        // console.log('✅ JWT token obtained:', token.substring(0, 50) + '...');
        // console.log('🔑 Returning Authorization header');
        return {
          'Authorization': `Bearer ${token}`,
        };
      } else {
        // console.warn('⚠️ No JWT token found in session');
        return {};
      }
    } catch (error) {
      // console.error('❌ Auth session error:', error);
      return {};
    }
  }

  // File upload for small files (<10MB) - upload via direct base64 to ingest endpoint
  async uploadSmallFile(file: File): Promise<UploadResponse> {
    try {
      // console.log(`Small file upload: Processing ${file.name} (${file.size} bytes)`);
      
      // Use direct upload to ingest endpoint (bypasses problematic Lambda function URL)
      // console.log('Using direct base64 upload to ingest endpoint...');
      return await this.uploadViaDirectUpload(file, `uploads/${Date.now()}-${file.name}`);
      
    } catch (error) {
      // console.error('Small file upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error}`,
      };
    }
  }

  /**
   * Sanitize filename for S3 - remove/replace problematic characters
   * This prevents issues with:
   * - Spaces (cause URL encoding issues with csv-extractor Lambda)
   * - Special characters that break S3 keys
   * - Multiple consecutive separators
   */
  private sanitizeFilename(filename: string): string {
    // Get extension
    const lastDot = filename.lastIndexOf('.');
    const name = lastDot > 0 ? filename.substring(0, lastDot) : filename;
    const ext = lastDot > 0 ? filename.substring(lastDot) : '';

    // Replace spaces with underscores
    // Remove special characters except underscore, hyphen, period
    const sanitized = name
      .replace(/\s+/g, '_')                    // spaces → underscores
      .replace(/[^a-zA-Z0-9_.-]/g, '')         // remove special chars
      .replace(/_{2,}/g, '_')                  // multiple underscores → single
      .replace(/^[_.-]+|[_.-]+$/g, '');        // trim leading/trailing _.-

    return sanitized + ext;
  }



  // Main upload method - S3 first, then trigger ingest lambda for all files
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {
    try {
      // Sanitize filename before upload to prevent S3 key issues
      const originalFilename = file.name;
      const sanitizedFilename = this.sanitizeFilename(originalFilename);

      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      // console.log(`Processing upload: "${originalFilename}" (${fileSizeMB}MB)`);

      // console.log('🚀 Using enhanced async upload system with S3 event processing');

      // console.log('Using S3-first upload, then triggering ingest lambda for processing');

      if (onProgress) onProgress(5);

      if (originalFilename !== sanitizedFilename) {
        // console.log(`📝 Sanitized filename: "${originalFilename}" → "${sanitizedFilename}"`);
      }

      // Step 1: Get presigned URL for S3 upload
      // console.log('🔗 Step 1: Getting presigned URL for S3 upload...');
      // console.log(`📋 File details: ${sanitizedFilename} (${file.size} bytes, ${file.type})`);

      const presignedResult = await this.getS3PresignedUrl(sanitizedFilename, file.type || 'application/octet-stream', file.size);
      
      if (!presignedResult.success || !presignedResult.uploadUrl) {
        // console.error('❌ Step 1 FAILED: Presigned URL generation failed');
        // console.error('❌ Error:', presignedResult.error);
        throw new Error(presignedResult.error || 'Failed to get presigned URL');
      }
      
      // console.log('✅ Step 1 SUCCESS: Presigned URL obtained');
      // console.log(`📍 S3 Key: ${presignedResult.s3Key}`);
      
      if (onProgress) onProgress(15);
      
      // Step 2: Upload directly to S3 using presigned URL
      // console.log('📤 Step 2: Uploading file directly to S3...');
      // console.log(`🔗 Upload URL: ${presignedResult.uploadUrl.substring(0, 100)}...`);
      
      const uploadStartTime = Date.now();
      const uploadResponse = await fetch(presignedResult.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });
      
      const uploadDuration = Date.now() - uploadStartTime;
      // console.log(`⏱️ S3 upload took ${uploadDuration}ms (${(uploadDuration/1000).toFixed(1)}s)`);
      
      if (!uploadResponse.ok) {
        // console.error('❌ Step 2 FAILED: S3 upload failed');
        // console.error(`❌ Status: ${uploadResponse.status} - ${uploadResponse.statusText}`);
        throw new Error(`S3 upload failed: ${uploadResponse.status} - ${uploadResponse.statusText}`);
      }
      
      // console.log('✅ Step 2 SUCCESS: File uploaded to S3');
      
      if (onProgress) onProgress(80);

      // Step 3: Processing is handled by S3 event triggers
      // Each file type has a dedicated Lambda triggered on upload

      if (onProgress) onProgress(100);
      
      // Return success for async upload pattern
      return {
        success: true,
        fileName: sanitizedFilename,
        message: `File uploaded successfully and is now queued for processing.`,
        s3Key: presignedResult.s3Key,
        uploadDuration: uploadDuration,
        async: true,
        warnings: [],
        errors: []
      };
      
    } catch (error) {
      // console.error('Upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error}`,
      };
    }
  }

  // List all files with proper API call via API Gateway
  async listFiles(): Promise<FileListResponse> {
    try {
      // Use auth headers for user-specific file listing
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(lambdaEndpoints.fileList, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // console.warn(`File list API returned ${response.status}: ${response.statusText}`);
        
        // Try to get error details
        let errorMessage = 'Unable to load files at this time';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Ignore JSON parsing errors
        }
        
        return {
          success: false,
          files: [],
          error: errorMessage
        };
      }

      const result = await response.json();
      // console.log('File list response:', result);
      
      // Map the API response format to our expected format
      const mappedFiles = (result.files || []).map((file: any) => {
        const isParquet = file.key?.endsWith('.parquet') ||
                         file.originalExtension === 'parquet' ||
                         file.currentExtension === 'parquet' ||
                         file.displayName?.toLowerCase().includes('parquet');

        return {
          fileName: file.displayName || file.key || 'Unknown',
          fileSize: file.size || 0,
          uploadDate: file.lastModified || new Date().toISOString(),
          lastModified: file.lastModified || new Date().toISOString(),
          contentType: isParquet ? 'application/octet-stream' : 'text/csv',
          originalExtension: file.originalExtension || (isParquet ? 'parquet' : null),
          currentExtension: file.currentExtension || (file.key?.split('.').pop() || 'unknown'),
          originalFilename: file.originalFilename || file.displayName || file.key,
          s3Key: file.key,  // Add the actual S3 key for analysis
          isInFolder: file.isInFolder || false,
          folderPath: file.folderPath || undefined,
          isEnhanced: file.isEnhanced || false,
          sizeMB: file.sizeMB || (file.size || 0) / (1024 * 1024),
          isMultipart: file.isMultipart || false,
          fileCount: file.fileCount || undefined,
          userDescription: file.userDescription || '',  // User-provided description
          sourceUrl: file.sourceUrl || '',  // Source URL if downloaded
          fileType: file.fileType || 'data'
        };
      });
      
      // Cache the files for analysis
      this.cachedFiles = mappedFiles;

      return {
        success: true,
        files: mappedFiles,
        error: result.error
      };
    } catch (error) {
      // console.error('File listing error:', error);
      
      return {
        success: false,
        files: [],
        error: 'Network error - unable to load files'
      };
    }
  }

  // Get processing status for files currently being processed
  async getProcessingStatus(): Promise<ProcessingStatusResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.processingStatus, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // console.warn(`Processing status API returned ${response.status}: ${response.statusText}`);

        let errorMessage = 'Unable to load processing status';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Ignore JSON parsing errors
        }

        return {
          success: false,
          processing_files: [],
          total: 0,
          timestamp: new Date().toISOString(),
          error: errorMessage
        };
      }

      const result = await response.json();
      // console.log('Processing status response:', result);
      // console.log('Processing files:', JSON.stringify(result.processing_files, null, 2));

      return result;
    } catch (error) {
      // console.error('Processing status error:', error);

      return {
        success: false,
        processing_files: [],
        total: 0,
        timestamp: new Date().toISOString(),
        error: 'Network error - unable to load processing status'
      };
    }
  }

  // Download file from URL
  async downloadFromUrl(url: string): Promise<{ success: boolean; message?: string; error?: string; filename?: string; download_id?: string }> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.urlDownloader, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Failed to download file from URL'
        };
      }

      // Only return download_id if the backend actually returned one
      // (supports progress tracking). Otherwise omit it so frontend knows
      // not to poll for progress.
      return result;
    } catch (error) {
      // console.error('URL download error:', error);
      return {
        success: false,
        error: 'Network error - unable to download from URL'
      };
    }
  }

  // Get download progress for a URL download
  async getDownloadProgress(download_id: string): Promise<{
    success: boolean;
    status?: 'starting' | 'downloading' | 'completed' | 'failed' | 'not_found';
    progress?: number;
    bytes_downloaded?: number;
    total_bytes?: number;
    filename?: string;
    speed_mbps?: number;
    eta_seconds?: number;
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(
        `${lambdaEndpoints.urlDownloadProgress}?download_id=${encodeURIComponent(download_id)}`,
        { headers }
      );

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: 'not_found',
          error: result.error || 'Failed to fetch download progress'
        };
      }

      return result;
    } catch (error) {
      // console.error('Download progress error:', error);
      return {
        success: false,
        status: 'not_found',
        error: 'Network error - unable to fetch download progress'
      };
    }
  }

  // Delete a file
  async deleteFile(fileName: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.fileDelete, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ fileName }),
      });

      return response.ok;
    } catch (error) {
      // console.error('Error deleting file:', error);
      return false;
    }
  }

  // Delete a failed upload and its error status
  async deleteFailedUpload(s3Key: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.fileDelete, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({
          s3_key: s3Key,
          delete_error_status: true
        }),
      });

      return response.ok;
    } catch (error) {
      // console.error('Error deleting failed upload:', error);
      return false;
    }
  }


  // Check if a file/folder should use the enhanced orchestrator
  private shouldUseOrchestrator(fileName: string, fileInfo?: FileInfo): boolean {
    // Use orchestrator for multipart files (folders with worker files)
    const isFolder = fileName.endsWith('/') ||
                     (fileInfo?.s3Key && fileInfo.s3Key.endsWith('/')) ||
                     (fileInfo?.isMultipart === true) ||
                     (fileInfo?.isInFolder === true);

    if (isFolder) {
      // console.log(`Using orchestrator endpoint for multipart file: ${fileName}`);
      return true;
    }

    // console.log(`Using regular analysis endpoint for single file: ${fileName}`);
    return false;
  }

  // Get folder path for chunked dataset
  private getChunkFolderPath(fileName: string, fileInfo?: FileInfo): string {
    if (fileName.endsWith('/')) {
      return fileName;
    }
    
    if (fileInfo?.s3Key) {
      // Extract folder path from chunk file path
      const key = fileInfo.s3Key;
      const lastSlash = key.lastIndexOf('/');
      if (lastSlash > 0) {
        return key.substring(0, lastSlash + 1);
      }
    }
    
    // Fallback: assume it's a folder
    return fileName.endsWith('/') ? fileName : `${fileName}/`;
  }

  // Analysis with caching support
  async runAnalysis(fileName: string, query: string, maxParallelism: number = 5, queryScope: 'first' | 'all' = 'all', processingLevel: 'file' | 'rowgroup' = 'file', analysisLimit: number = 1000, resultLimit: number = 1000): Promise<AnalysisResult | null> {
    try {
      // console.log(`Running analysis on ${fileName} with query: ${query}, maxParallelism: ${maxParallelism}, queryScope: ${queryScope}, processingLevel: ${processingLevel}, resultLimit: ${resultLimit}`);

      // Find the file in our cached files list to get the actual S3 key
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileName);
      let fileKey = fileInfo?.s3Key || fileName;

      // console.log('File info found:', fileInfo);
      // console.log('Using file key:', fileKey);

      // Check if this should use the enhanced orchestrator (chunks or large files)
      const useOrchestrator = this.shouldUseOrchestrator(fileName, fileInfo);
      // console.log('Should use orchestrator:', useOrchestrator);

      if (useOrchestrator) {
        // Use enhanced orchestrator for chunks or large parquet files
        return this.runOrchestratorAnalysis(fileName, query, fileInfo, maxParallelism, queryScope, processingLevel, analysisLimit, resultLimit);
      }
      
      // If no S3 key found, this might be a user-specific file
      // The file key should already be in the correct format from the file list API
      if (!fileKey || (!fileKey.startsWith('users/') && !fileKey.startsWith('data/'))) {
        // console.warn('File key not in expected format, using fallback');
        // For user-specific files, we need the full path
        fileKey = fileName.includes('.parquet') ? fileName : `${fileName}.parquet`;
      }
      
      // Prepare the analysis payload according to BigSplice Lambda guide
      // Check if query already assigns to result variable (search queries and full scripts)
      const hasResultAssignment = query.includes('result =') || query.includes('search_conditions');
      const analysis_code = hasResultAssignment ? query : `result = ${query}`;

      // Large result sets (visualize asks for up to 100K rows) must not be returned
      // inline — Lambda hard-caps synchronous responses at 6MB and crashes with a 413
      // beyond it. Ask the Lambda to export gzipped TSV to S3 and hand back a
      // presigned URL instead.
      const useS3Export = resultLimit > 1000;

      const payload = {
        bucket: 'bigsplice-us-east-1',
        file_key: fileKey,
        analysis_code: analysis_code,
        analysis_limit: 1000,
        ...(useS3Export ? {
          export_results: true,
          output_format: 'tsv',
          enable_gzip: true,
        } : {})
      };
      
      // console.log('Analysis payload:', payload);
      
      // Include authentication headers for user-specific analysis
      const authHeaders = await this.getAuthHeaders();
      // console.log('🔍 Auth headers received:', authHeaders);
      
      const finalHeaders = {
        ...authHeaders,
        'Content-Type': 'application/json'
      };
      // console.log('📤 Final headers being sent:', finalHeaders);
      
      // Try cache first (if cache endpoint is configured)
      let response;
      let isCached = false;
      
      if (lambdaEndpoints.queryCache && !lambdaEndpoints.queryCache.includes('placeholder')) {
        try {
          // console.log('🔍 Checking query cache...');
          response = await fetch(lambdaEndpoints.queryCache, {
            method: 'POST',
            headers: finalHeaders,
            body: JSON.stringify(payload)
          });
          
          if (response.ok) {
            const cacheResult = await response.json();
            if (cacheResult.success) {
              isCached = cacheResult.cached;
              // console.log(isCached ? '✅ Cache hit!' : '❌ Cache miss, executed query');
              // Use the cached/executed result
              const result = cacheResult;
              return this.transformAnalysisResult(result, query, isCached);
            }
          } else {
            // console.warn(`Cache endpoint returned ${response.status}: ${response.statusText}`);
          }
        } catch (cacheError) {
          // console.warn('Cache request failed, falling back to direct analysis:', cacheError);
        }
      }
      
      // Fallback to direct analysis if cache is not available or failed
      if (!response || !response.ok) {
        // console.log('📊 Running direct analysis...');
        response = await fetch(lambdaEndpoints.analysis, {
          method: 'POST',
          headers: finalHeaders,
          body: JSON.stringify(payload)
        });
      }
      
      if (!response.ok) {
        // console.error(`Analysis API returned ${response.status}: ${response.statusText}`);
        throw new Error(`Analysis failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      // console.log('Analysis response:', result);

      // S3-export mode: no inline rows; surface the presigned URL in the
      // s3Results shape that shouldFetchFromS3/fetchAndDecompressS3 consume.
      if (useS3Export && result.export?.presigned_url) {
        const transformed = this.transformAnalysisResult(result, query, false);
        transformed.s3Only = true;
        transformed.s3Results = {
          display_url: result.export.presigned_url,
          download_url: result.export.presigned_url,
          s3_key: result.export.key || '',
          compressed_size_mb: result.export.final_size_mb || 0,
          full_row_count: result.export.rows || 0,
        };
        return transformed;
      }

      return this.transformAnalysisResult(result, query, false);
      
    } catch (error) {
      // console.error('Analysis error:', error);
      throw error; // Re-throw to show error in UI
    }
  }

  // Enhanced orchestrator analysis for chunk folders and large parquet files
  async runOrchestratorAnalysis(fileName: string, query: string, fileInfo?: FileInfo, maxParallelism: number = 5, queryScope: 'first' | 'all' = 'all', processingLevel: 'file' | 'rowgroup' = 'file', analysisLimit: number = 1000, resultLimit: number = 1000): Promise<AnalysisResult | null> {
    try {
      // console.log(`Running orchestrator analysis on ${fileName} with query: ${query}, maxParallelism: ${maxParallelism}, queryScope: ${queryScope}, processingLevel: ${processingLevel}, analysisLimit: ${analysisLimit}, resultLimit: ${resultLimit}`);

      // Determine if this is a single file or folder/multipart dataset
      const isFolder: boolean = fileName.endsWith('/') ||
                                Boolean(fileInfo?.s3Key && fileInfo.s3Key.includes('part_')) ||
                                Boolean(fileInfo?.s3Key && fileInfo.s3Key.includes('_worker_')) ||
                                Boolean(fileInfo?.isMultipart);

      // Prepare the analysis payload for enhanced orchestrator
      // Check if query already assigns to result variable (search queries and full scripts)
      const hasResultAssignment = query.includes('result =') || query.includes('search_conditions');
      const analysis_code = hasResultAssignment ? query : `result = ${query}`;

      let payload: any = {
        analysis_code: analysis_code,
        analysis_limit: analysisLimit,
        result_limit: resultLimit,  // Maximum rows to store in S3 (1000 for search, 10000 for export)
        bucket: 'bigsplice-us-east-1',
        max_parallelism: maxParallelism,
        first_part_only: queryScope === 'first',  // Send first_part_only flag to orchestrator
        force_rowgroup_mode: processingLevel === 'rowgroup'  // Send force_rowgroup_mode flag to orchestrator
      };
      
      if (isFolder) {
        // For chunk folders
        const folderPath = this.getChunkFolderPath(fileName, fileInfo);
        // console.log('Processing chunk folder:', folderPath);
        payload.folder_path = folderPath;
      } else {
        // For large single parquet files
        const fileKey = fileInfo?.s3Key || fileName;
        // console.log('Processing large parquet file:', fileKey);
        payload.file_path = fileKey;
      }
      
      // console.log('Orchestrator analysis payload:', payload);
      
      // Include authentication headers
      const authHeaders = await this.getAuthHeaders();
      // console.log('🔍 Auth headers for orchestrator analysis:', authHeaders);
      
      const finalHeaders = {
        ...authHeaders,
        'Content-Type': 'application/json'
      };
      
      // console.log('📤 Calling enhanced orchestrator endpoint:', lambdaEndpoints.chunkAnalysis);
      
      const response = await fetch(lambdaEndpoints.chunkAnalysis, {
        method: 'POST',
        headers: finalHeaders,
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Orchestrator analysis failed:', response.status, errorText);
        throw new Error(`Orchestrator analysis failed: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      // console.log('Orchestrator analysis response:', result);
      
      // Transform the orchestrator analysis result
      return this.transformOrchestratorAnalysisResult(result, query, fileName, isFolder);
      
    } catch (error) {
      // console.error('Orchestrator analysis error:', error);
      throw error;
    }
  }

  // Transform orchestrator analysis results (handles both chunk folders and row group processing)
  private transformOrchestratorAnalysisResult(result: any, query: string, fileName: string, isFolder: boolean): AnalysisResult {
    const analysisResults = result.analysis_results || {};
    const executionTime = result.orchestrator_processing_time_ms || result.total_processing_time_ms || 0;
    
    // Convert the column-based format to row-based format for table display
    const transformedResults = [];
    if (analysisResults && typeof analysisResults === 'object') {
      const columns = Object.keys(analysisResults);
      if (columns.length > 0) {
        const firstColumn = analysisResults[columns[0]];
        const rowCount = Array.isArray(firstColumn) ? firstColumn.length : 1;
        
        for (let i = 0; i < rowCount; i++) {
          const row: any = {};
          columns.forEach(col => {
            const values = analysisResults[col];
            row[col] = Array.isArray(values) ? values[i] : values;
          });
          transformedResults.push(row);
        }
      }
    }
    
    // Add processing metadata to the result - ONLY if using async processing
    let chunkInfo;
    
    // Check if this result uses async processing (row group parallel or chunk folder)
    const usesAsyncProcessing = result.uses_async_processing !== false && (
      result.query_folder || 
      result.total_row_groups || 
      result.total_chunks ||
      isFolder
    );
    
    if (usesAsyncProcessing) {
      if (isFolder) {
        // Chunk folder processing metadata
        chunkInfo = {
          totalChunks: result.total_chunks || 0,
          successfulChunks: result.successful_chunks || 0,
          failedChunks: result.failed_chunks || 0,
          totalRows: result.total_rows || transformedResults.length,
          totalColumns: result.total_columns || 0,
          folderPath: result.folder_path || result.path || fileName,
          processingType: 'chunk_folder' as const
        };
      } else {
        // Row group processing metadata
        chunkInfo = {
          totalChunks: result.total_row_groups || 0,
          successfulChunks: result.successful_row_groups || 0,
          failedChunks: result.failed_row_groups || 0,
          totalRows: result.total_rows || transformedResults.length,
          totalColumns: result.total_columns || 0,
          folderPath: result.query_folder || result.path || fileName, // Use query_folder as folderPath for async results
          processingType: 'row_group_parallel' as const,
          metadata: {
            ...result.metadata, // Include parquet metadata for row group processing
            query_folder: result.query_folder // Add query_folder for async polling
          }
        };
      }
    } else {
      // Primary lambda succeeded - no async processing, no chunkInfo needed
      chunkInfo = undefined;
    }
    
    // console.log('Orchestrator analysis summary:', chunkInfo);
    
    return {
      query: query,
      result: transformedResults,
      executionTime: executionTime,
      rowCount: transformedResults.length,
      totalRowsAvailable: result.total_rows_available,
      resultTruncated: result.result_truncated,
      isCached: false,
      s3Only: result.s3_only === true, // True when >1000 rows - frontend must fetch from S3
      chunkInfo: chunkInfo, // Add chunk metadata
      s3Results: result.s3_results ? {
        display_url: result.s3_results.display_url,
        download_url: result.s3_results.download_url,
        s3_key: result.s3_results.s3_key,
        compressed_size_mb: result.s3_results.compressed_size_mb,
        full_row_count: result.s3_results.full_row_count || result.s3_results.total_dataset_rows
      } : undefined
    };
  }

  // Helper method to transform analysis results
  private transformAnalysisResult(result: any, query: string, isCached: boolean): AnalysisResult {
    // Transform the response to match our AnalysisResult interface
    const analysisResults = result.analysis_results || {};
    const executionTime = result.execution_time || result.timings?.total_execution_ms || 0;
    
    // Convert the column-based format to row-based format for table display
    const transformedResults = [];
    if (analysisResults && typeof analysisResults === 'object') {
      const columns = Object.keys(analysisResults);
      if (columns.length > 0) {
        const firstColumn = analysisResults[columns[0]];
        const rowCount = Array.isArray(firstColumn) ? firstColumn.length : 1;
        
        for (let i = 0; i < rowCount; i++) {
          const row: any = {};
          columns.forEach(col => {
            const values = analysisResults[col];
            row[col] = Array.isArray(values) ? values[i] : values;
          });
          transformedResults.push(row);
        }
      }
    }
    
    return {
      query: query,
      result: transformedResults,
      executionTime: executionTime,
      rowCount: transformedResults.length,
      isCached: isCached
    };
  }

  // Fallback method: Upload via direct S3 approach (requires backend Lambda modification)
  private async uploadViaDirectS3(file: File, s3Key: string): Promise<UploadResponse> {
    try {
      // console.log('Direct S3 upload: This approach requires a Lambda function that accepts file content and uploads to S3');
      
      // For now, return an informative error message
      return {
        success: false,
        error: 'File upload feature requires backend configuration. The current Lambda functions expect files to already be in S3. Please contact support to enable direct file upload functionality.'
      };
    } catch (error) {
      return {
        success: false,
        error: `Direct S3 upload failed: ${error}`
      };
    }
  }

  // Lambda-based presigned URL upload method
  private async uploadViaPresignedUrl(file: File): Promise<UploadResponse> {
    try {
      // console.log(`Lambda presigned URL upload: Processing ${file.name} (${file.size} bytes)`);
      
      // Generate unique S3 key
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const uniqueFileName = `uploads/${timestamp}-${file.name}`;
      
      // Step 1: Get presigned URL from new upload Lambda function
      const headers = await this.getAuthHeaders();
      const presignedResponse = await fetch(lambdaEndpoints.fileUpload, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'generate_presigned_url',
          fileName: uniqueFileName,
          contentType: file.type || 'text/csv',
          fileSize: file.size,
        }),
      });
      
      if (!presignedResponse.ok) {
        const errorText = await presignedResponse.text();
        // console.error('Lambda presigned URL error:', errorText);
        
        // If Lambda doesn't support presigned URLs, fall back to direct upload
        // console.log('Lambda presigned URL failed, trying direct upload approach...');
        return await this.uploadViaDirectUpload(file, uniqueFileName);
      }
      
      const presignedData = await presignedResponse.json();
      
      if (!presignedData.success || !presignedData.uploadUrl) {
        // console.log('Lambda presigned URL response invalid, trying direct upload...');
        return await this.uploadViaDirectUpload(file, uniqueFileName);
      }
      
      // console.log(`Lambda presigned URL: Got presigned URL for ${uniqueFileName}`);
      
      // Step 2: Upload file directly to S3 using presigned URL
      const uploadResponse = await fetch(presignedData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'text/csv',
        },
      });
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`S3 upload failed: ${uploadResponse.status} - ${errorText}`);
      }
      
      // console.log(`Lambda presigned URL: Successfully uploaded to S3: ${uniqueFileName}`);
      
      // Step 3: Trigger processing via ingest Lambda
      const processingResult = await this.triggerProcessing(uniqueFileName);
      if (!processingResult.success) {
        // console.warn('Processing trigger failed, but file was uploaded to S3:', processingResult.error);
        // Still return success since file is uploaded
      }
      
      return {
        success: true,
        fileName: file.name,
        message: `File ${file.name} uploaded successfully to BigSplice and processing started.`,
      };
      
    } catch (error) {
      // console.error('Lambda presigned URL upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error}`
      };
    }
  }

  // Direct upload method - uploads file content directly to Lambda
  private async uploadViaDirectUpload(file: File, s3Key: string): Promise<UploadResponse> {
    try {
      // console.log(`Direct upload to Lambda: Processing ${s3Key}`);
      
      // Convert file to base64 for transmission
      const base64Content = await this.fileToBase64(file);
      const headers = await this.getAuthHeaders();
      
      // Try different Lambda endpoints and payload formats (prioritize working endpoints)
      const uploadStrategies = [
        {
          name: 'Enhanced Ingest Lambda - direct upload',
          endpoint: lambdaEndpoints.ingest,
          payload: {
            fileContent: base64Content,
            fileName: s3Key,
            contentType: file.type || 'text/csv',
            fileSize: file.size,
          }
        },
        {
          name: 'Large Upload Lambda with upload action',
          endpoint: lambdaEndpoints.largeUpload,
          payload: {
            action: 'upload_file',
            fileName: s3Key,
            fileContent: base64Content,
            contentType: file.type || 'text/csv',
            fileSize: file.size,
          }
        }
      ];
      
      for (const strategy of uploadStrategies) {
        // console.log(`Trying: ${strategy.name}`);
        
        try {
          const response = await fetch(strategy.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(strategy.payload),
          });
          
          if (response.ok) {
            const result = await response.json();
            // console.log(`✅ Success with ${strategy.name}:`, result);
            
            return {
              success: true,
              fileName: file.name,
              message: `File ${file.name} uploaded successfully to BigSplice and processing started.`,
            };
          } else {
            const errorText = await response.text();
            // console.log(`❌ ${strategy.name} failed: ${response.status} - ${errorText}`);
          }
        } catch (strategyError) {
          // console.log(`❌ ${strategy.name} error:`, strategyError);
        }
      }
      
      // If all strategies fail, return error
      throw new Error('All upload strategies failed. The Lambda functions may not support direct file uploads.');
      
    } catch (error) {
      // console.error('Direct upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error}`
      };
    }
  }

  // Lambda proxy method - uses Lambda function to handle S3 upload
  private async uploadViaLambdaProxy(file: File, s3Key: string): Promise<UploadResponse> {
    try {
      // console.log(`Lambda proxy upload: Processing ${s3Key}`);
      
      // Convert file to base64 for transmission
      const base64Content = await this.fileToBase64(file);
      const headers = await this.getAuthHeaders();
      
      // Use the large upload Lambda as a proxy for S3 operations
      const payload = {
        action: 'proxy_upload',
        fileName: s3Key,
        fileContent: base64Content,
        contentType: file.type || 'text/csv',
        fileSize: file.size,
        bucket: 'bigsplice-us-east-1'
      };
      
      // console.log('Lambda proxy upload: Sending to large upload endpoint...');
      
      const response = await fetch(lambdaEndpoints.largeUpload, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Lambda proxy upload error:', errorText);
        
        // If proxy upload fails, try the base64 fallback
        // console.log('Lambda proxy failed, trying base64 fallback...');
        return await this.uploadViaBase64Fallback(file, s3Key);
      }
      
      const result = await response.json();
      // console.log('Lambda proxy upload: Success response:', result);
      
      // If the Lambda uploaded to S3, trigger processing
      if (result.success || result.uploaded) {
        const processingResult = await this.triggerProcessing(s3Key);
        if (!processingResult.success) {
          // console.warn('Processing trigger failed, but file was uploaded:', processingResult.error);
        }
      }
      
      return {
        success: true,
        fileName: file.name,
        message: `File ${file.name} uploaded successfully to BigSplice and processing started.`,
      };
      
    } catch (error) {
      // console.error('Lambda proxy upload error:', error);
      
      // Try base64 fallback as last resort
      // console.log('Lambda proxy failed with error, trying base64 fallback...');
      return await this.uploadViaBase64Fallback(file, s3Key);
    }
  }

  // Fallback method using base64 upload to ingest endpoint
  private async uploadViaBase64Fallback(file: File, s3Key: string): Promise<UploadResponse> {
    try {
      // console.log(`Base64 fallback upload: Processing ${s3Key}`);
      
      // Convert file to base64
      const base64Content = await this.fileToBase64(file);
      const headers = await this.getAuthHeaders();
      
      // Try different payload formats to see what works
      const payloadFormats = [
        {
          name: 'Format 1: Direct upload with file_content',
          payload: {
            action: 'upload_with_content',
            source_key: s3Key,
            bucket: 'bigsplice-us-east-1',
            file_content: base64Content,
            content_type: file.type || 'text/csv'
          }
        },
        {
          name: 'Format 2: Processing with inline content',
          payload: {
            source_key: s3Key,
            bucket: 'bigsplice-us-east-1',
            inline_content: base64Content,
            content_type: file.type || 'text/csv'
          }
        }
      ];
      
      for (const format of payloadFormats) {
        // console.log(`Trying ${format.name}...`);
        
        try {
          const response = await fetch(lambdaEndpoints.ingest, {
            method: 'POST',
            headers,
            body: JSON.stringify(format.payload),
          });
          
          if (response.ok) {
            const result = await response.json();
            // console.log(`Base64 fallback upload: Success with ${format.name}`, result);
            
            return {
              success: true,
              fileName: file.name,
              message: `File ${file.name} uploaded successfully to BigSplice and processing started.`,
            };
          } else {
            const errorText = await response.text();
            // console.log(`${format.name} failed:`, errorText);
          }
        } catch (formatError) {
          // console.log(`${format.name} error:`, formatError);
        }
      }
      
      // If all formats fail, return a helpful error
      return {
        success: false,
        error: `File upload feature requires backend configuration. The current Lambda functions expect files to already be in S3. Please contact support to enable direct file upload functionality.`
      };
      
    } catch (error) {
      // console.error('Base64 fallback upload error:', error);
      return {
        success: false,
        error: `Fallback upload failed: ${error}`
      };
    }
  }

  // Direct S3 upload using AWS SDK with Amplify credentials (DEPRECATED)
  private async uploadViaManualS3(file: File, s3Key: string): Promise<UploadResponse> {
    // This method is deprecated - use presigned URL approach instead
    // console.log('Direct S3 upload method is deprecated, falling back to presigned URL');
    return await this.uploadViaPresignedUrl(file);
  }

  // Helper function to trigger processing after S3 upload
  private async triggerProcessing(s3Key: string): Promise<{success: boolean, processing_details?: any, warnings?: string[], errors?: string[], rows_processed?: number, rows_skipped?: number, error?: string}> {
    try {
      // console.log(`Triggering processing for: ${s3Key}`);
      
      const response = await fetch(lambdaEndpoints.ingest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_key: s3Key,
          bucket: 'bigsplice-us-east-1',
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `Processing trigger failed: ${response.status} - ${errorText}`
        };
      }
      
      const result = await response.json();
      // console.log('Processing trigger result:', result);
      
      return {
        success: result.success || false,
        processing_details: result.processing_details,
        warnings: result.warnings,
        errors: result.errors,
        rows_processed: result.rows_processed,
        rows_skipped: result.rows_skipped,
        error: result.error,
      };
    } catch (error) {
      // console.error('Error triggering processing:', error);
      return {
        success: false,
        error: `Failed to trigger processing: ${error}`
      };
    }
  }

  // Helper function to get S3 presigned URL from our dedicated Lambda
  async getS3PresignedUrl(fileName: string, contentType: string, fileSize: number): Promise<{success: boolean, uploadUrl?: string, s3Key?: string, error?: string}> {
    try {
      // console.log(`🔐 Getting S3 presigned URL for: ${fileName} (${fileSize} bytes)`);
      
      // console.log('🔑 Obtaining authentication headers...');
      const headers = await this.getAuthHeaders();
      // console.log('✅ Auth headers obtained successfully');
      
      const response = await fetch(lambdaEndpoints.presignedUrlGenerator, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fileName: fileName,
          contentType: contentType,
          fileSize: fileSize,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Presigned URL error:', errorText);
        
        // Handle authentication errors specifically
        if (response.status === 401 || response.status === 403) {
          return {
            success: false,
            error: 'Authentication required - please log in again'
          };
        }
        
        return {
          success: false,
          error: `Failed to get presigned URL: ${response.status} - ${errorText}`
        };
      }
      
      const result = await response.json();
      // console.log('Presigned URL response:', result);
      
      if (result.success && result.uploadUrl) {
        return {
          success: true,
          uploadUrl: result.uploadUrl,
          s3Key: result.s3Key
        };
      } else {
        return {
          success: false,
          error: result.error || 'No upload URL in response'
        };
      }
    } catch (error) {
      // console.error('Error getting S3 presigned URL:', error);
      return {
        success: false,
        error: `Error generating presigned URL: ${error}`
      };
    }
  }

  // Helper function to get presigned URL for S3 upload (legacy method)
  private async getPresignedUploadUrl(s3Key: string, contentType: string): Promise<{success: boolean, uploadUrl?: string, error?: string}> {
    try {
      // console.log(`Getting presigned URL for: ${s3Key}`);
      const headers = await this.getAuthHeaders();
      
      // Use the large upload Lambda to get presigned URLs
      const response = await fetch(lambdaEndpoints.largeUpload, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'get_presigned_url',
          fileName: s3Key,
          contentType: contentType,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Presigned URL error:', errorText);
        return {
          success: false,
          error: `Failed to get presigned URL: ${errorText}`
        };
      }
      
      const result = await response.json();
      // console.log('Presigned URL response:', result);
      
      if (result.uploadUrl) {
        return {
          success: true,
          uploadUrl: result.uploadUrl
        };
      } else {
        return {
          success: false,
          error: 'No upload URL in response'
        };
      }
    } catch (error) {
      // console.error('Error getting presigned URL:', error);
      return {
        success: false,
        error: `Error generating presigned URL: ${error}`
      };
    }
  }


  // Helper function to convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  // Generate query using OpenAI
  async generateQuery(
    prompt: string, 
    headers: string[], 
    sampleRows: any[][], 
    fileName?: string, 
    modelConfig?: {
      provider: 'gpt-5' | 'claude' | 'bedrock';
      claudeModel?: 'claude-3-5-sonnet-20241022' | 'claude-3-haiku-20240307' | 'claude-3-opus-20240229' | 'claude-haiku-4-5';
      bedrockModel?: 'openai.gpt-oss-120b-1:0' | 'openai.gpt-oss-20b-1:0';
      gpt5ReasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
    }
  ): Promise<QueryGenerationResult> {
    try {
      const config = modelConfig || { provider: 'gpt-5' };
      const { provider, claudeModel, bedrockModel, gpt5ReasoningEffort } = config;

      let modelName = 'GPT-5';
      let endpoint = lambdaEndpoints.queryGenerator;

      if (provider === 'claude') {
        endpoint = lambdaEndpoints.queryGeneratorClaude;
        switch (claudeModel) {
          case 'claude-haiku-4-5':
            modelName = 'Claude 4.5 Haiku';
            break;
          case 'claude-3-haiku-20240307':
            modelName = 'Claude 3 Haiku';
            break;
          case 'claude-3-opus-20240229':
            modelName = 'Claude 3 Opus';
            break;
          case 'claude-3-5-sonnet-20241022':
          default:
            modelName = 'Claude 3.5 Sonnet';
            break;
        }
      } else if (provider === 'bedrock') {
        endpoint = lambdaEndpoints.queryGeneratorBedrock;
        if (bedrockModel === 'openai.gpt-oss-20b-1:0') {
          modelName = 'Secure GPT // AWS Bedrock (GPT-OSS-20B)';
        } else {
          modelName = 'Secure GPT // AWS Bedrock (GPT-OSS-120B)';
        }
      } else {
        modelName = `GPT-5 (${gpt5ReasoningEffort || 'minimal'} reasoning)`;
      }
      
      // console.log(`Generating query with ${modelName}:`, { prompt, headers, sampleRows, fileName, config });

      const payload: any = {
        prompt,
        headers,
        sample_rows: sampleRows,
        file_key: fileName || 'unknown',
        bucket: 'bigsplice-us-east-1'
      };

      // Add model-specific parameters
      if (provider === 'claude' && claudeModel) {
        payload.claude_model = claudeModel;
      } else if (provider === 'bedrock' && bedrockModel) {
        payload.bedrock_model = bedrockModel;
      } else if (provider === 'gpt-5' && gpt5ReasoningEffort) {
        payload.reasoning_effort = gpt5ReasoningEffort;
      }

      // Get auth headers for authenticated endpoints
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        // console.error('Query generation failed:', response.status, errorText);
        return {
          success: false,
          error: `Query generation failed: ${response.status} - ${errorText}`
        };
      }

      const result = await response.json();
      // console.log('Query generation result:', result);

      if (result.success) {
        return {
          success: true,
          query: result.query,
          explanation: result.explanation,
          detailed_explanation: result.detailed_explanation,
          debug: result.debug
        };
      } else {
        return {
          success: false,
          error: result.error || 'Unknown error occurred',
          debug: result.debug
        };
      }
    } catch (error) {
      // console.error('Error generating query:', error);
      return {
        success: false,
        error: `Failed to generate query: ${error}`
      };
    }
  }

  // Query Results Management
  async getQueryResults(queryFolder: string, action: 'list' | 'aggregate' | 'get_result' = 'list', rowGroupIndex?: number): Promise<QueryResultsResponse> {
    try {
      const authHeaders = await this.getAuthHeaders();
      
      const payload: any = {
        query_folder: queryFolder,
        action: action
      };
      
      if (action === 'get_result' && rowGroupIndex !== undefined) {
        payload.row_group_index = rowGroupIndex;
      }
      
      const response = await fetch(lambdaEndpoints.queryResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      // console.error('Error fetching query results:', error);
      throw error;
    }
  }

  async listUserQueries(userId?: string): Promise<QueryResultsInfo[]> {
    try {
      const authHeaders = await this.getAuthHeaders();
      
      // List all query folders for the user
      const payload = {
        action: 'list_user_queries',
        user_id: userId
      };
      
      const response = await fetch(lambdaEndpoints.queryResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.queries || [];
    } catch (error) {
      // console.error('Error listing user queries:', error);
      return [];
    }
  }

  // Query History Management
  async getQueryHistory(limit: number = 50, lastKey?: string, fileName?: string): Promise<QueryHistoryResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });
      
      if (lastKey) {
        params.append('last_key', lastKey);
      }
      
      if (fileName) {
        params.append('file_name', fileName);
      }

      // Get authentication headers (required for JWT authentication)
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(`${lambdaEndpoints.queryHistory}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // console.error('Error getting query history:', error);
      return {
        success: false,
        error: `Failed to get query history: ${error}`
      };
    }
  }

  async saveQueryHistory(queryData: {
    query_type: 'user' | 'ai_generated';
    query_text: string;
    prompt?: string;
    file_name: string;
    model_info?: string;
    execution_time: number;
    success: boolean;
    result_count: number;
    error_message?: string;
  }): Promise<{ success: boolean; query_id?: string; error?: string }> {
    try {
      // Get authentication headers (required for JWT authentication)
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.queryHistory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(queryData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // console.error('Error saving query history:', error);
      return {
        success: false,
        error: `Failed to save query history: ${error}`
      };
    }
  }

  async deleteQueryHistory(queryId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get authentication headers (required for JWT authentication)
      const authHeaders = await this.getAuthHeaders();

      // Build URL with query parameters
      const params = new URLSearchParams({
        query_id: queryId
      });

      const response = await fetch(`${lambdaEndpoints.queryHistory}?${params.toString()}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        // console.error(`Delete failed: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // console.error('Error deleting query history:', error);
      return {
        success: false,
        error: `Failed to delete query history: ${error}`
      };
    }
  }

  // Folder Management Methods

  /**
   * Create a new folder
   */
  async createFolder(folderPath: string): Promise<{ success: boolean; error?: string; folderPath?: string }> {
    try {
      // console.log(`Creating folder: ${folderPath}`);
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.fileMove, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          action: 'create_folder',
          folderPath: folderPath
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // console.error(`Failed to create folder: ${response.status}`, result);
        return {
          success: false,
          error: result.error || 'Failed to create folder'
        };
      }

      // console.log('Folder created successfully:', result);
      return result;
    } catch (error) {
      // console.error('Error creating folder:', error);
      return {
        success: false,
        error: `Failed to create folder: ${error}`
      };
    }
  }

  /**
   * Move a file to a different folder
   */
  async moveFile(sourceFile: string, targetFolder: string): Promise<{ success: boolean; error?: string }> {
    try {
      // console.log(`Moving file: ${sourceFile} to folder: ${targetFolder}`);
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.fileMove, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          action: 'move_file',
          sourceFile: sourceFile,
          targetFolder: targetFolder
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // console.error(`Failed to move file: ${response.status}`, result);
        return {
          success: false,
          error: result.error || 'Failed to move file'
        };
      }

      // console.log('File moved successfully:', result);
      return result;
    } catch (error) {
      // console.error('Error moving file:', error);
      return {
        success: false,
        error: `Failed to move file: ${error}`
      };
    }
  }

  /**
   * Delete a folder and optionally its contents
   */
  async deleteFolder(folderPath: string, recursive: boolean = false): Promise<{ success: boolean; error?: string }> {
    try {
      // console.log(`Deleting folder: ${folderPath} (recursive: ${recursive})`);
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.fileMove, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          action: 'delete_folder',
          folderPath: folderPath,
          recursive: recursive
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // console.error(`Failed to delete folder: ${response.status}`, result);
        return {
          success: false,
          error: result.error || 'Failed to delete folder'
        };
      }

      // console.log('Folder deleted successfully:', result);
      return result;
    } catch (error) {
      // console.error('Error deleting folder:', error);
      return {
        success: false,
        error: `Failed to delete folder: ${error}`
      };
    }
  }

  /**
   * List all folders for the current user
   */
  async listFolders(): Promise<{ success: boolean; folders: string[]; error?: string }> {
    try {
      // console.log('Listing folders...');
      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.fileMove, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          action: 'list_folders'
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // console.error(`Failed to list folders: ${response.status}`, result);
        return {
          success: false,
          folders: [],
          error: result.error || 'Failed to list folders'
        };
      }

      // console.log('Folders listed successfully:', result);
      return result;
    } catch (error) {
      // console.error('Error listing folders:', error);
      return {
        success: false,
        folders: [],
        error: `Failed to list folders: ${error}`
      };
    }
  }

  /**
   * Parse folder structure from file list
   */
  getFolderStructure(files: FileInfo[]): Map<string, FileInfo[]> {
    const folderMap = new Map<string, FileInfo[]>();

    files.forEach(file => {
      // Extract folder path from S3 key
      const key = file.s3Key || file.fileName;
      const parts = key.split('/');

      // Get folder path (everything except the filename)
      let folderPath = '';
      if (parts.length > 4) {
        // Skip 'users/{user_id}/data/' and get intermediate folders
        folderPath = parts.slice(3, -1).join('/');
      }

      if (!folderMap.has(folderPath)) {
        folderMap.set(folderPath, []);
      }

      folderMap.get(folderPath)!.push(file);
    });

    return folderMap;
  }

  // ============================================================================
  // S3 Transfer Methods (External S3 to BigSplice)
  // ============================================================================

  /**
   * Save S3 credentials for external bucket access
   */
  async saveS3Credentials(
    accessKey: string,
    secretKey: string,
    bucketName: string,
    prefix?: string
  ): Promise<import('../types/s3Transfer').S3CredentialsSaveResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.s3Credentials, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessKey,
          secretKey,
          bucketName,
          prefix: prefix || '',
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error saving S3 credentials:', error);
      return {
        success: false,
        error: `Failed to save credentials: ${error}`,
      };
    }
  }

  /**
   * Get S3 credentials status (not the actual credentials)
   */
  async getS3CredentialsStatus(): Promise<import('../types/s3Transfer').S3CredentialsStatus> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.s3Credentials, {
        method: 'GET',
        headers,
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error getting S3 credentials status:', error);
      return {
        success: false,
        has_credentials: false,
        error: `Failed to get credentials status: ${error}`,
      };
    }
  }

  /**
   * Delete saved S3 credentials
   */
  async deleteS3Credentials(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.s3Credentials, {
        method: 'DELETE',
        headers,
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting S3 credentials:', error);
      return {
        success: false,
        error: `Failed to delete credentials: ${error}`,
      };
    }
  }

  /**
   * Browse files in external S3 bucket using saved credentials
   */
  async browseS3Files(
    bucketName?: string,
    prefix?: string,
    continuationToken?: string,
    maxKeys: number = 1000
  ): Promise<import('../types/s3Transfer').S3FilesResponse> {
    try {
      const headers = await this.getAuthHeaders();

      // Build query parameters
      const params = new URLSearchParams();
      if (bucketName) params.append('bucketName', bucketName);
      if (prefix) params.append('prefix', prefix);
      if (continuationToken) params.append('continuationToken', continuationToken);
      params.append('maxKeys', maxKeys.toString());

      const url = `${lambdaEndpoints.s3FileBrowser}?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error browsing S3 files:', error);
      return {
        success: false,
        error: `Failed to browse files: ${error}`,
      };
    }
  }

  /**
   * Transfer files from external S3 to BigSplice
   */
  async transferS3Files(
    fileKeys: string[],
    bucketName?: string
  ): Promise<import('../types/s3Transfer').S3TransferResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(lambdaEndpoints.s3Transfer, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucketName,
          fileKeys,
        }),
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error transferring S3 files:', error);
      return {
        success: false,
        error: `Failed to transfer files: ${error}`,
      };
    }
  }

  // ============================================================================
  // GCP Cloud Storage Methods (External GCP to BigSplice)
  // ============================================================================

  async saveGcpCredentials(
    accessKey: string,
    secretKey: string,
    bucketName: string,
    prefix?: string
  ): Promise<import('../types/s3Transfer').S3CredentialsSaveResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.gcpCredentials, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessKey, secretKey, bucketName, prefix: prefix || '' }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error saving GCP credentials:', error);
      return { success: false, error: `Failed to save credentials: ${error}` };
    }
  }

  async getGcpCredentialsStatus(): Promise<import('../types/s3Transfer').S3CredentialsStatus> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.gcpCredentials, { method: 'GET', headers });
      return await response.json();
    } catch (error) {
      console.error('Error getting GCP credentials status:', error);
      return { success: false, has_credentials: false, error: `Failed to get credentials status: ${error}` };
    }
  }

  async deleteGcpCredentials(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.gcpCredentials, { method: 'DELETE', headers });
      return await response.json();
    } catch (error) {
      console.error('Error deleting GCP credentials:', error);
      return { success: false, error: `Failed to delete credentials: ${error}` };
    }
  }

  async browseGcpFiles(
    bucketName?: string,
    prefix?: string,
    continuationToken?: string,
    maxKeys: number = 1000
  ): Promise<import('../types/s3Transfer').S3FilesResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams();
      if (bucketName) params.append('bucketName', bucketName);
      if (prefix) params.append('prefix', prefix);
      if (continuationToken) params.append('continuationToken', continuationToken);
      params.append('maxKeys', maxKeys.toString());
      const url = `${lambdaEndpoints.gcpFileBrowser}?${params.toString()}`;
      const response = await fetch(url, { method: 'GET', headers });
      return await response.json();
    } catch (error) {
      console.error('Error browsing GCP files:', error);
      return { success: false, error: `Failed to browse files: ${error}` };
    }
  }

  async transferGcpFiles(
    fileKeys: string[],
    bucketName?: string
  ): Promise<import('../types/s3Transfer').S3TransferResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.gcpTransfer, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucketName, fileKeys }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error transferring GCP files:', error);
      return { success: false, error: `Failed to transfer files: ${error}` };
    }
  }

  // ============================================================
  // SQL Ingestion Methods
  // ============================================================

  /**
   * List all SQL connections for the current user
   */
  async listSQLConnections(): Promise<import('../types/sqlIngestion').SQLConnectionsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.sqlConnections, {
        method: 'GET',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error listing SQL connections:', error);
      return { success: false, error: `Failed to list connections: ${error}` };
    }
  }

  /**
   * Create a new SQL connection
   */
  async createSQLConnection(
    connection: import('../types/sqlIngestion').SQLConnectionInput
  ): Promise<import('../types/sqlIngestion').SQLConnectionsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.sqlConnections, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(connection),
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating SQL connection:', error);
      return { success: false, error: `Failed to create connection: ${error}` };
    }
  }

  /**
   * Update an existing SQL connection
   */
  async updateSQLConnection(
    connectionId: string,
    updates: Partial<import('../types/sqlIngestion').SQLConnectionInput>
  ): Promise<import('../types/sqlIngestion').SQLConnectionsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlConnections}/${connectionId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating SQL connection:', error);
      return { success: false, error: `Failed to update connection: ${error}` };
    }
  }

  /**
   * Delete a SQL connection
   */
  async deleteSQLConnection(connectionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlConnections}/${connectionId}`, {
        method: 'DELETE',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error deleting SQL connection:', error);
      return { success: false, error: `Failed to delete connection: ${error}` };
    }
  }

  /**
   * Test a SQL connection (with provided credentials or saved connection)
   */
  async testSQLConnection(
    config: import('../types/sqlIngestion').SQLConnectionInput | { connectionId: string }
  ): Promise<import('../types/sqlIngestion').SQLTestResult> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.sqlTest, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      return await response.json();
    } catch (error) {
      console.error('Error testing SQL connection:', error);
      return { success: false, error: `Failed to test connection: ${error}` };
    }
  }

  /**
   * Test a SQL query (returns preview rows)
   */
  async testSQLQuery(
    connectionId: string,
    query: string
  ): Promise<import('../types/sqlIngestion').SQLQueryTestResult> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlTest}/query`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, query }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error testing SQL query:', error);
      return { success: false, error: `Failed to test query: ${error}` };
    }
  }

  /**
   * List all SQL jobs for the current user
   */
  async listSQLJobs(): Promise<import('../types/sqlIngestion').SQLJobsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.sqlJobs, {
        method: 'GET',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error listing SQL jobs:', error);
      return { success: false, error: `Failed to list jobs: ${error}` };
    }
  }

  /**
   * Create a new SQL job
   */
  async createSQLJob(
    job: import('../types/sqlIngestion').SQLJobInput
  ): Promise<import('../types/sqlIngestion').SQLJobsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.sqlJobs, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(job),
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating SQL job:', error);
      return { success: false, error: `Failed to create job: ${error}` };
    }
  }

  /**
   * Update an existing SQL job
   */
  async updateSQLJob(
    jobId: string,
    updates: Partial<import('../types/sqlIngestion').SQLJobInput> & { status?: string }
  ): Promise<import('../types/sqlIngestion').SQLJobsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlJobs}/${encodeURIComponent(jobId)}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating SQL job:', error);
      return { success: false, error: `Failed to update job: ${error}` };
    }
  }

  /**
   * Delete a SQL job
   */
  async deleteSQLJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlJobs}/${encodeURIComponent(jobId)}`, {
        method: 'DELETE',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error deleting SQL job:', error);
      return { success: false, error: `Failed to delete job: ${error}` };
    }
  }

  /**
   * Run a SQL job immediately
   */
  async runSQLJob(jobId: string): Promise<import('../types/sqlIngestion').SQLJobRunResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlJobs}/${encodeURIComponent(jobId)}/run`, {
        method: 'POST',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error running SQL job:', error);
      return { success: false, error: `Failed to run job: ${error}` };
    }
  }

  /**
   * Get job run history
   */
  async getSQLJobHistory(jobId: string): Promise<import('../types/sqlIngestion').SQLJobHistoryResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlJobs}/${encodeURIComponent(jobId)}/history`, {
        method: 'GET',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error getting SQL job history:', error);
      return { success: false, error: `Failed to get job history: ${error}` };
    }
  }

  /**
   * Get logs for a specific job run
   */
  async getSQLJobRunLogs(jobId: string, runId: string): Promise<import('../types/sqlIngestion').SQLLogsResponse> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlJobs}/${encodeURIComponent(jobId)}/history/${encodeURIComponent(runId)}/logs`, {
        method: 'GET',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error getting SQL job run logs:', error);
      return { success: false, error: `Failed to get logs: ${error}` };
    }
  }

  // ============================================================
  // SQL Introspection Methods (Table Browser)
  // ============================================================

  async listDatabaseTables(connectionId: string, options?: {
    includeSize?: boolean;
    includeRowCount?: boolean;
  }): Promise<{
    success: boolean;
    tables?: { name: string; display_name?: string; schema?: string; size_bytes?: number; row_count?: number }[];
    count?: number;
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const params = new URLSearchParams({ connection_id: connectionId });
      if (options?.includeSize) params.append('include_size', 'true');
      if (options?.includeRowCount) params.append('include_row_count', 'true');
      const response = await fetch(`${lambdaEndpoints.sqlIntrospect}/tables?${params.toString()}`, {
        method: 'GET',
        headers,
      });
      return await response.json();
    } catch (error) {
      console.error('Error listing tables:', error);
      return { success: false, error: `Failed to list tables: ${error}` };
    }
  }

  async getTableSchema(connectionId: string, tableName: string): Promise<{
    success: boolean;
    table_name?: string;
    columns?: {
      name: string;
      type: string;
      nullable: boolean;
      default: any;
      is_datetime: boolean;
      is_numeric: boolean;
      is_primary_key: boolean;
    }[];
    primary_key?: string[];
    indexes?: { name: string; definition?: string; column?: string }[];
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${lambdaEndpoints.sqlIntrospect}/schema?connection_id=${connectionId}&table=${encodeURIComponent(tableName)}`,
        { method: 'GET', headers }
      );
      return await response.json();
    } catch (error) {
      console.error('Error getting table schema:', error);
      return { success: false, error: `Failed to get schema: ${error}` };
    }
  }

  async previewTableData(
    connectionId: string,
    tableName: string,
    options?: {
      columns?: string[];
      limit?: number;
      offset?: number;
      order_by?: string;
      order_desc?: boolean;
    }
  ): Promise<{
    success: boolean;
    columns?: string[];
    rows?: Record<string, any>[];
    limit?: number;
    offset?: number;
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${lambdaEndpoints.sqlIntrospect}/preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          connection_id: connectionId,
          table: tableName,
          ...options,
        }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error previewing table:', error);
      return { success: false, error: `Failed to preview table: ${error}` };
    }
  }

  // ==================== Data Introspection Methods ====================

  /**
   * Get AI-generated insights for a data file
   */
  async getFileInsights(
    fileKey: string,
    forceRefresh: boolean = false
  ): Promise<{
    success: boolean;
    insights?: {
      file_key: string;
      generated_at: string;
      cached: boolean;
      data_quality: {
        overall_score: number;
        null_percentage: number;
        duplicate_percentage: number;
        issues: string[];
      };
      statistical_summary: {
        row_count: number;
        column_count: number;
        columns: Array<{
          name: string;
          type: string;
          null_count: number;
          unique_count: number;
          min?: number | string | null;
          max?: number | string | null;
          mean?: number | null;
        }>;
      };
      business_insights: {
        summary: string;
        recommendations: string[];
        potential_use_cases: string[];
      };
    };
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileKey);
      const actualKey = fileInfo?.s3Key || fileKey;

      const response = await fetch(lambdaEndpoints.introspect, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: actualKey,
          force_refresh: forceRefresh,
          action: 'insights',
        }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error getting file insights:', error);
      return { success: false, error: `Failed to get insights: ${error}` };
    }
  }

  /**
   * Generate a chart from natural language prompt
   */
  async generateChart(
    fileKey: string,
    prompt: string
  ): Promise<{
    success: boolean;
    chart?: {
      chart_type: 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'radar';
      title: string;
      data: Record<string, unknown>[];
      config: {
        xKey: string;
        yKey: string;
        color?: string;
      };
      query?: string;
    };
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileKey);
      const actualKey = fileInfo?.s3Key || fileKey;

      const response = await fetch(lambdaEndpoints.introspect, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: actualKey,
          action: 'chart',
          prompt: prompt,
        }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error generating chart:', error);
      return { success: false, error: `Failed to generate chart: ${error}` };
    }
  }

  /**
   * Generate automatic charts based on data analysis
   */
  async getAutoCharts(
    fileKey: string
  ): Promise<{
    success: boolean;
    charts?: Array<{
      chart_type: 'bar' | 'line' | 'area' | 'pie' | 'scatter' | 'radar';
      title: string;
      data: Record<string, unknown>[];
      config: {
        xKey: string;
        yKey: string;
        color?: string;
      };
    }>;
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileKey);
      const actualKey = fileInfo?.s3Key || fileKey;

      const response = await fetch(lambdaEndpoints.introspect, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: actualKey,
          action: 'auto_charts',
        }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error generating auto charts:', error);
      return { success: false, error: `Failed to generate auto charts: ${error}` };
    }
  }

  /**
   * Chat with your data - conversational data analysis
   */
  async chatWithData(
    fileKey: string,
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<{
    success: boolean;
    response?: string;
    suggested_questions?: string[];
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileKey);
      const actualKey = fileInfo?.s3Key || fileKey;

      const response = await fetch(lambdaEndpoints.introspect, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: actualKey,
          action: 'chat',
          message: message,
          history: history,
        }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error chatting with data:', error);
      return { success: false, error: `Failed to chat with data: ${error}` };
    }
  }

  /**
   * Get column distributions for stats dashboard sparkline visualizations
   */
  async getColumnDistributions(
    fileKey: string,
    forceRefresh: boolean = false
  ): Promise<{
    success: boolean;
    distributions?: {
      file_key: string;
      total_rows: number;
      sampled_rows: number;
      generated_at: string;
      cached: boolean;
      columns: Array<{
        name: string;
        dtype: string;
        viz_type: 'numeric' | 'categorical' | 'boolean' | 'high_cardinality' | 'temporal' | 'unknown';
        null_count: number;
        non_null_count: number;
        null_ratio: number;
        distribution: Record<string, unknown> | null;
      }>;
    };
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();
      const fileInfo = this.cachedFiles?.find(f => f.fileName === fileKey);
      const actualKey = fileInfo?.s3Key || fileKey;

      const response = await fetch(lambdaEndpoints.introspect, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_key: actualKey,
          force_refresh: forceRefresh,
          action: 'distributions',
        }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error getting column distributions:', error);
      return { success: false, error: `Failed to get distributions: ${error}` };
    }
  }

  // ===== Chat Orchestrator Methods =====

  /**
   * Send a message to the chat orchestrator with polling support
   */
  async sendChatMessage(request: {
    message: string;
    sessionId?: string;
    context?: Record<string, unknown>;
    selectedFile?: string;
    selectedFileKey?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    resultType?: 'text' | 'table' | 'file_list' | 'visualization' | 'help' | 'confirmation' | 'upload_helper' | 'upload_guidance' | 'error';
    resultData?: unknown;
    processingLayer?: 'static' | 'bedrock' | 'claude';
    executionTime?: number;
    error?: string;
  }> {
    try {
      const headers = await this.getAuthHeaders();

      if (!lambdaEndpoints.chatOrchestrator) {
        // Fallback: process locally with static commands only
        return this.processLocalChatCommand(request.message);
      }

      // Start the chat job
      const response = await fetch(lambdaEndpoints.chatOrchestrator, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Chat request failed: ${response.status} - ${errorText}`);
      }

      const initialResult = await response.json();

      // If completed immediately, return the result
      if (initialResult.status === 'completed') {
        return initialResult.result || initialResult;
      }

      // If processing, poll for results
      if (initialResult.status === 'processing' && initialResult.job_id) {
        return await this.pollChatJob(initialResult.job_id, headers);
      }

      // Direct result (no polling needed)
      return initialResult;
    } catch (error) {
      console.error('Chat service error:', error);
      return {
        success: false,
        error: `Failed to process message: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Poll for chat job completion
   */
  private async pollChatJob(
    jobId: string,
    headers: Record<string, string>,
    maxAttempts = 60, // 60 * 2s = 120s max
    intervalMs = 2000
  ): Promise<{
    success: boolean;
    message?: string;
    resultType?: 'text' | 'table' | 'file_list' | 'visualization' | 'help' | 'confirmation' | 'upload_helper' | 'upload_guidance' | 'error';
    resultData?: unknown;
    processingLayer?: 'static' | 'bedrock' | 'claude';
    executionTime?: number;
    error?: string;
  }> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));

      try {
        const pollResponse = await fetch(
          `${lambdaEndpoints.chatOrchestrator}?job_id=${jobId}`,
          {
            method: 'GET',
            headers,
          }
        );

        if (!pollResponse.ok) {
          continue; // Retry on error
        }

        const pollResult = await pollResponse.json();

        if (pollResult.status === 'completed') {
          return pollResult.result || pollResult;
        }

        if (pollResult.status === 'failed') {
          return {
            success: false,
            error: pollResult.result?.error || 'Chat processing failed',
          };
        }

        // Still processing, continue polling
      } catch (error) {
        console.warn(`Poll attempt ${attempt + 1} failed:`, error);
        // Continue polling
      }
    }

    // Timeout
    return {
      success: false,
      error: 'Request timed out. Please try again.',
    };
  }

  /**
   * Process command locally (fallback when Lambda not available)
   */
  private processLocalChatCommand(message: string): {
    success: boolean;
    message?: string;
    resultType?: 'text' | 'table' | 'file_list' | 'visualization' | 'help' | 'confirmation' | 'upload_helper' | 'upload_guidance' | 'error';
    resultData?: unknown;
    processingLayer?: 'static' | 'bedrock' | 'claude';
  } {
    const lowerMessage = message.toLowerCase().trim();

    // Help command
    if (lowerMessage === 'help' || lowerMessage === 'commands') {
      return {
        success: true,
        resultType: 'help',
        resultData: {
          commands: this.getChatHelpCommands(),
        },
        processingLayer: 'static',
      };
    }

    // Default response for unknown commands
    return {
      success: true,
      message:
        'Chat orchestrator is not configured. Please deploy the chat-orchestrator Lambda and add the endpoint to aws-config.ts.',
      resultType: 'text',
      processingLayer: 'static',
    };
  }

  /**
   * Get help commands list for chat
   */
  private getChatHelpCommands() {
    return [
      {
        name: 'Files',
        commands: [
          { command: 'list files', description: 'Show all your files', example: 'list files' },
          { command: 'list files in [folder]', description: 'Show files in a folder', example: 'list files in reports' },
          { command: 'delete [filename]', description: 'Delete a file (with confirmation)', example: 'delete old_data.parquet' },
          { command: 'move [file] to [folder]', description: 'Move file to folder', example: 'move report.parquet to archives' },
          { command: 'create folder [name]', description: 'Create a new folder', example: 'create folder Q4_Reports' },
          { command: 'search [query]', description: 'Search for files', example: 'search sales' },
        ],
      },
      {
        name: 'Analysis',
        commands: [
          { command: 'select [filename]', description: 'Select file for analysis', example: 'select sales_data' },
          { command: 'columns', description: 'Show columns in selected file', example: 'columns' },
          { command: 'head [n]', description: 'Show first N rows', example: 'head 10' },
          { command: 'count rows', description: 'Count total rows', example: 'count rows' },
          { command: 'run [query]', description: 'Run Polars query', example: 'run df.filter(pl.col("amount") > 100)' },
        ],
      },
      {
        name: 'Visualization',
        commands: [
          { command: 'visualize as bars', description: 'Create bar chart', example: 'visualize as bars by category' },
          { command: 'visualize as donut', description: 'Create donut chart', example: 'visualize as donut by region' },
          { command: 'visualize as scatter', description: 'Create scatter plot', example: 'visualize as scatter' },
        ],
      },
      {
        name: 'Upload',
        commands: [
          { command: 'upload help', description: 'Show upload options', example: 'upload help' },
          { command: 'upload from url [link]', description: 'Download from URL', example: 'upload from url https://...' },
        ],
      },
      {
        name: 'Other',
        commands: [
          { command: 'my usage', description: 'Show usage statistics', example: 'my usage' },
          { command: 'help', description: 'Show this help', example: 'help' },
        ],
      },
    ];
  }

  /**
   * Get command suggestions for chat autocomplete
   */
  getChatCommandSuggestions(input: string): Array<{
    command: string;
    description: string;
    example: string;
    category: 'files' | 'analysis' | 'visualization' | 'upload' | 'settings';
  }> {
    const commands = [
      // File Management
      { command: 'list files', description: 'Show all your files', example: 'list files', category: 'files' as const },
      { command: 'list files in', description: 'Show files in a folder', example: 'list files in reports', category: 'files' as const },
      { command: 'delete', description: 'Delete a file', example: 'delete old_data.parquet', category: 'files' as const },
      { command: 'move', description: 'Move file to folder', example: 'move report.parquet to archives', category: 'files' as const },
      { command: 'create folder', description: 'Create a new folder', example: 'create folder Q4_Reports', category: 'files' as const },
      { command: 'search', description: 'Search for files', example: 'search sales', category: 'files' as const },

      // Analysis
      { command: 'select', description: 'Select file for analysis', example: 'select sales_data', category: 'analysis' as const },
      { command: 'columns', description: 'Show columns in selected file', example: 'columns', category: 'analysis' as const },
      { command: 'schema', description: 'Show file schema', example: 'schema', category: 'analysis' as const },
      { command: 'head', description: 'Show first N rows', example: 'head 10', category: 'analysis' as const },
      { command: 'count rows', description: 'Count total rows', example: 'count rows', category: 'analysis' as const },
      { command: 'run', description: 'Execute Polars query', example: 'run df.head(100)', category: 'analysis' as const },
      { command: 'analyze', description: 'AI-powered analysis', example: 'analyze top selling products', category: 'analysis' as const },

      // Visualization
      { command: 'visualize as bars', description: 'Create 3D bar chart', example: 'visualize as bars by category', category: 'visualization' as const },
      { command: 'visualize as donut', description: 'Create donut chart', example: 'visualize as donut by region', category: 'visualization' as const },
      { command: 'visualize as pie', description: 'Create pie chart', example: 'visualize as pie', category: 'visualization' as const },
      { command: 'visualize as scatter', description: 'Create scatter plot', example: 'visualize as scatter x=price y=quantity', category: 'visualization' as const },
      { command: 'visualize as line', description: 'Create line chart', example: 'visualize as line', category: 'visualization' as const },

      // Upload
      { command: 'upload help', description: 'Show upload options', example: 'upload help', category: 'upload' as const },
      { command: 'upload from url', description: 'Download from URL', example: 'upload from url https://...', category: 'upload' as const },

      // Settings
      { command: 'my usage', description: 'Show usage statistics', example: 'my usage', category: 'settings' as const },
      { command: 'api keys', description: 'Manage API keys', example: 'api keys', category: 'settings' as const },
      { command: 'help', description: 'Show available commands', example: 'help', category: 'settings' as const },
    ];

    const lowerInput = input.toLowerCase().trim();

    if (!lowerInput) {
      // Return top commands when empty
      return commands.slice(0, 6);
    }

    return commands
      .filter(
        (cmd) =>
          cmd.command.toLowerCase().includes(lowerInput) ||
          cmd.description.toLowerCase().includes(lowerInput)
      )
      .slice(0, 6);
  }

  // ===== AI Column Inference =====

  async inferAiCell(
    prompt: string,
    rowData: Record<string, unknown>,
    columnName: string,
    bedrockModel: string = 'openai.gpt-oss-120b-1:0',
    mode: 'ai' | 'web_search' = 'ai',
  ): Promise<{ success: boolean; value?: string; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(lambdaEndpoints.aiColumnInference, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          row_data: rowData,
          column_name: columnName,
          bedrock_model: bedrockModel,
          mode,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: `Inference failed: ${response.status} - ${errorText}` };
      }

      const result = await response.json();
      return result;
    } catch (error: any) {
      return { success: false, error: error.message || 'Network error' };
    }
  }

}

export const lambdaService = new LambdaService();